import { Component, HostBinding, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ToolbarService } from '../../services/toolbar.service';
import { ConfigService } from '../../services/config.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { DeviceService } from '../../services/device.service';
import { extractFirstNameAndLastName } from '../../utils';
import { FormControl, Validators } from '@angular/forms';
import { VersionService } from '../../services/version.service';
import { environment } from '../../../environments/environment';
import { Subscription } from 'rxjs';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { SwUpdate } from '@angular/service-worker';
import {LocationService} from '../../services/location.service';
import { validatePhoneNumber } from '../../util/phone-number-validator';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit {

  // set class on host element
  @HostBinding('class') classes = 'app-content';

  phoneNumberFormControl: FormControl<string>;
  phoneNumber = '';

  // form controls
  scannerSizeMultiplierFormControl = new FormControl(0);
  lowPowerScanningFormControl = new FormControl(false);
  hideItemsBarFormControl = new FormControl(false);

  deviceId = '';
  firstName = '';
  lastName = '';
  userName = '';

  envName = '';
  appVersion = '';
  checkingForUpdate = false;

  locationState = '';
  locationPermissionStatus = '';

  scannerInactivityTimeoutSecs: number;
  private scannerInactivityTimeoutSubscription: Subscription;

  constructor(
    private locationService: LocationService,
    private location: Location,
    private config: ConfigService,
    private device: DeviceService,
    private toolbar: ToolbarService,
    private version: VersionService,
    private auth: AngularFireAuth,
    private swUpdate: SwUpdate,
    private remoteConfig: AngularFireRemoteConfig,
  ) {
  }

  ngOnInit(): void {
    this.appVersion = this.version.appVersion();
    this.envName = environment.name;
    this.phoneNumber = this.config.getPhoneNumber() || '';
    this.phoneNumberFormControl = new FormControl(
      this.phoneNumber,
      [
        Validators.required,
        (ctrl) => validatePhoneNumber(ctrl.value) ? null : { 'error': 'phone number invalid' }
      ]
    );
    this.toolbar.setToolbarConfig({
      backAction: () => {
        if (this.phoneNumberFormControl.valid) {
          this.location.back();
        }
      },
      showSettings: false,
      showCall: true,
      showRefresh: false,
      showLogout: true
    });
    this.scannerSizeMultiplierFormControl.setValue(this.config.getScannerSizeMultiplier());
    this.lowPowerScanningFormControl.setValue(this.config.lowPowerScanning);
    this.hideItemsBarFormControl.setValue(this.config.hideItemsBar);
    this.auth.user.subscribe(u => {
      if (u) {
        this.userName = u.email;
        const nameComponents = extractFirstNameAndLastName(u.displayName);
        this.firstName = nameComponents ? nameComponents[0] : '';
        this.lastName = nameComponents ? nameComponents[1] : '';
      }
    });
    this.deviceId = this.device.deviceId;
    this.scannerInactivityTimeoutSubscription = this.remoteConfig.numbers.scannerInactivityTimeout.subscribe(v => {
      this.scannerInactivityTimeoutSecs = v / 1000;
    });

    this.locationService.state.subscribe((state) => {
      if (typeof state === 'string') {
        this.locationState = state;
      } else {
        this.locationState = `${state.latitude.toFixed(4)}° N, ${state.longitude.toFixed(4)}° E`;
      }
    })
    this.locationPermissionStatus = '?';
    if (navigator.permissions && typeof navigator.permissions.query === 'function') {
      navigator.permissions.query({ name: 'geolocation' })
        .then(permissionStatus => {
          this.locationPermissionStatus = permissionStatus.state;
        })
        .catch(err => {
          this.locationPermissionStatus = 'ERR';
        });
    }
  }

  formatScannerSizeMultipleThumbLabel(value: number): string {
    return `${value}%`;
  }

  save(): void {
    if (this.phoneNumberFormControl.valid) {
      this.config.savePhoneNumber(this.phoneNumberFormControl.value);
      this.config.saveScannerSizeMultiplier(this.scannerSizeMultiplierFormControl.value);
      this.config.setLowPowerScanning(this.lowPowerScanningFormControl.value);
      this.config.setHideItemsBar(this.hideItemsBarFormControl.value);
      this.location.back();
    }
  }

  checkForUpdates(): void {
    this.checkingForUpdate = true;
    this.swUpdate.checkForUpdate()
      .then((result) => {
        console.log(`Update check result, new version available: ${result ? 'YES' : 'NO'}`);
      })
      .catch((err) => {
        console.error(`Update check failed with error`, err);
      })
      .finally(() => {
        this.checkingForUpdate = false;
      });
  }
}
